import type { AssetDto } from '@nsb/core';
import { Fragment, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { ApiError, api, errorText, queryClient, upload, useApi } from '../../api.ts';
import { DropZone } from '../../components/DropZone.tsx';
import { useFeedbackRecord } from '../../components/feedback-context.ts';
import { Button, Confirm, ErrorLine, Field, LoadError, Loading, Page, Tag, TextInput, Title, Toggle } from '../../components/ui.tsx';
import { useUnsaved } from '../../components/useUnsaved.ts';
import { changedLabels } from '../../unsaved.ts';
import { ProjectCrumb } from './common.tsx';
import { assetsKey, useProject, useProjectId } from './hooks.ts';
import { dateWords, easternToday } from '../../days.ts';
import { typeOf } from '../../fileTypes.ts';

/**
 * A project's files (feature spec "client page, files" §2; Jason, 2026-10-03:
 * "a general bucket for project assets and drive links so that we can see
 * everything in one place"). Anything uploaded and every link to Drive, NSB's
 * alone until shared. Each opens on its own page, where both sides can mark it
 * up and talk about it (the review file page).
 */

/** One word for what it is: PDF, JPEG, DWG, Drive. A word before an icon (guide §20.2). */
function kindWord(a: AssetDto): string {
  if (a.kind === 'link') return /(^|\.)google\.com$/.test(hostOf(a.url)) ? 'Drive' : 'Link';
  if (a.contentType === 'application/pdf') return 'PDF';
  if (a.contentType && /^image\/(jpeg|png|webp|gif|tiff|heic)$/.test(a.contentType)) return a.contentType.slice(6).toUpperCase();
  const ext = a.name.includes('.') ? a.name.split('.').pop()!.toUpperCase() : '';
  return ext && ext.length <= 5 ? ext : 'File';
}

function hostOf(url: string | null): string {
  try {
    return url ? new URL(url).hostname : '';
  } catch {
    return '';
  }
}

function size(bytes: number | null): string | null {
  if (bytes === null) return null;
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`;
}

const day = (iso: string) => dateWords(iso, 'date');
const today = easternToday;

type Filter = 'all' | 'shared' | 'nsb';

export function Files() {
  const projectId = useProjectId();
  const { data: project } = useProject(projectId);
  const { data, error } = useApi<AssetDto[]>(assetsKey(projectId), `/projects/${projectId}/assets`);
  const [filter, setFilter] = useState<Filter>('all');
  useFeedbackRecord(projectId, project ? { type: 'project', id: projectId, label: `${project.name} — files` } : null);
  if (error) return <LoadError error={error} />;
  if (!data || !project) {
    return (
      <Page>
        <Loading />
      </Page>
    );
  }
  const staff = project.viewerRole === 'contractor';
  const shown = data.filter((a) => filter === 'all' || (filter === 'shared') === a.shared);
  const shared = data.filter((a) => a.shared).length;
  return (
    <Page wide>
      <ProjectCrumb project={project} />
      <Title
        how={
          staff
            ? {
                line: 'Everything on this project in one place.',
                intent: 'The project’s files and links, NSB’s and the client’s, each a thing both sides can open, mark up and ask about once shared.',
                steps: ['Drop files here, or add a link to Drive.', 'Yours are yours alone until you share them; the client’s are on the project for both of you.', 'Open one to mark it up or ask about it.'],
              }
            : {
                line: 'Everything on your project in one place.',
                intent: 'What NSB shares with you, and what you add, kept together.',
                steps: ['Drop a file here, or add a link.', 'Open one to look at it, mark it up, or ask about it.', 'NSB sees what you add.'],
              }
        }
      >
        Files
      </Title>
      <AddFiles projectId={projectId} staff={staff} />

      {data.length === 0 ? (
        <p className="mt-10 text-ink-3">{staff ? 'Nothing yet. Add files, or a link to Drive.' : 'Nothing here yet. Add a file, or a link, and NSB sees it too.'}</p>
      ) : (
        <section className="mt-10">
          <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2 border-b border-ink/40 pb-2">
            <h2 className="text-[1.375rem] sm:text-[1.5rem]">
              <span className="num">{data.length}</span> {data.length === 1 ? 'item' : 'items'}
            </h2>
            {staff ? (
              <div className="flex gap-5 text-sm" role="group" aria-label="Show">
                {(
                  [
                    ['all', `All ${data.length}`],
                    ['shared', `Shared ${shared}`],
                    ['nsb', `NSB only ${data.length - shared}`],
                  ] as const
                ).map(([k, label]) => (
                  <button
                    key={k}
                    type="button"
                    aria-pressed={filter === k}
                    onClick={() => setFilter(k)}
                    className={`min-h-11 ${filter === k ? 'text-ink underline decoration-ink underline-offset-[0.22em]' : 'link text-ink-2 hover:text-ink'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <ul className="divide-y divide-rule border-b border-rule">
            {shown.map((a) => (
              <AssetRow key={a.id} projectId={projectId} asset={a} staff={staff} />
            ))}
          </ul>
        </section>
      )}
    </Page>
  );
}

/** Files up as they are, one after another; and a link to Drive. NSB's land its own unless shared; a client's are on the project for both. */
function AddFiles({ projectId, staff }: { projectId: string; staff: boolean }) {
  const [sending, setSending] = useState<string | null>(null);
  const [linking, setLinking] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const send = async (files: File[]) => {
    setErr(null);
    for (const [i, file] of files.entries()) {
      setSending(files.length > 1 ? `Sending ${i + 1} of ${files.length}: ${file.name}` : `Sending ${file.name}`);
      try {
        queryClient.setQueryData(assetsKey(projectId), await upload<AssetDto[]>(`/projects/${projectId}/assets?name=${encodeURIComponent(file.name)}`, file, typeOf(file)));
      } catch (e) {
        setErr(`${file.name}: ${e instanceof ApiError && e.status === 413 ? 'too big to send. Keep files under 100 MB, or add a link to it in Drive.' : errorText(e)}`);
        break;
      }
    }
    setSending(null);
  };

  return (
    <div className="mt-8 max-w-3xl">
      <DropZone
        label="Drop files here"
        busy={sending !== null}
        onFiles={(files) => void send(files)}
        hint={
          sending ? (
            <span className="motion-safe:animate-pulse">{sending}</span>
          ) : staff ? (
            'Any kind, up to 100 MB each, kept as it came: drawings, PDFs, photos, sheets. Pictures can be marked up; anything can be talked about.'
          ) : (
            'Any kind, up to 100 MB each: photos of the site, plans, pictures you like. NSB sees what you add.'
          )
        }
      >
        {!linking ? (
          <Button tone="quiet" onClick={() => setLinking(true)}>
            Add a link instead
          </Button>
        ) : null}
      </DropZone>
      <ErrorLine error={err} />
      {linking ? <LinkForm projectId={projectId} staff={staff} onDone={() => setLinking(false)} /> : null}
    </div>
  );
}

function LinkForm({ projectId, staff, onDone }: { projectId: string; staff: boolean; onDone: () => void }) {
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [dated, setDated] = useState('');
  const [note, setNote] = useState('');
  const [share, setShare] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const add = async () => {
    setBusy(true);
    setErr(null);
    try {
      queryClient.setQueryData(
        assetsKey(projectId),
        await api<AssetDto[]>(`/projects/${projectId}/assets/links`, { body: { url: url.trim(), name: name.trim(), dated: dated || null, note: note.trim() || null, shared: share } }),
      );
      onDone();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-6 max-w-2xl border border-rule bg-sheet p-4 sm:p-5">
      <p className="label mb-4">A link</p>
      <Field label="Link" hint="A Drive file or folder, or any web page. It stays where it is.">
        {(id) => <TextInput id={id} type="url" inputMode="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://drive.google.com/…" />}
      </Field>
      <Field label="Name">{(id) => <TextInput id={id} value={name} onChange={(e) => setName(e.target.value)} />}</Field>
      <Field label="The day it is from" hint="Optional. When it is older than today: a survey, a signed proposal.">
        {(id) => <TextInput id={id} type="date" max={today()} value={dated} onChange={(e) => setDated(e.target.value)} />}
      </Field>
      <Field label="Note" hint="Optional.">
        {(id) => <textarea id={id} rows={2} className="field resize-y" value={note} onChange={(e) => setNote(e.target.value)} />}
      </Field>
      {staff ? <Toggle checked={share} onChange={setShare} label="Share it with the client" hint="They can open it and talk about it. You can stop sharing it later." /> : null}
      <div className="mt-3 flex gap-5">
        <Button busy={busy} disabled={!url.trim() || !name.trim()} onClick={() => void add()}>
          Add it
        </Button>
        <Button tone="quiet" onClick={onDone}>
          Cancel
        </Button>
      </div>
      <ErrorLine error={err} />
    </div>
  );
}

function AssetRow({ projectId, asset: a, staff }: { projectId: string; asset: AssetDto; staff: boolean }) {
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState<'remove' | 'unshare' | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const run = async (fn: () => Promise<AssetDto[]>) => {
    setBusy(true);
    setErr(null);
    try {
      queryClient.setQueryData(assetsKey(projectId), await fn());
      // The client's home counts what is shared; an iteration lists its files.
      void queryClient.invalidateQueries({ queryKey: ['project', projectId], predicate: (q) => q.queryKey[2] !== 'assets' });
      setConfirming(null);
      return true;
    } catch (e) {
      setErr(errorText(e));
      return false;
    } finally {
      setBusy(false);
    }
  };
  const patch = (body: Record<string, unknown>) => run(() => api<AssetDto[]>(`/projects/${projectId}/assets/${a.id}`, { method: 'PATCH', body }));
  const open = `/p/${projectId}/reviews/files/${a.id}?from=files`;
  // Numbers in mono, words in words (guide §20.2); the kind is on the tile beside it.
  const facts: ReactNode[] = [
    a.sizeBytes !== null ? (
      <span key="size" className="num">
        {size(a.sizeBytes)}
      </span>
    ) : null,
    <Fragment key="day">
      {a.dated ? 'from' : 'added'} <span className="num">{day(a.dated ?? a.createdAt)}</span>
    </Fragment>,
    a.review ? `${a.review.phase}, iteration ${a.review.iteration}` : null,
    a.onBoard ? 'on the design board' : null,
    a.sourceUrl && a.kind === 'file' ? `from ${hostOf(a.sourceUrl).replace(/^www\./, '')}` : null,
    a.threads ? (
      <Fragment key="threads">
        <span className="num">{a.threads}</span> comment{a.threads === 1 ? '' : 's'}
        {a.openThreads ? (
          <>
            , <span className="num">{a.openThreads}</span> open
          </>
        ) : null}
      </Fragment>
    ) : null,
  ].filter(Boolean);

  return (
    <li className="py-4">
      <div className="flex gap-4">
        <Link to={open} className="shrink-0" tabIndex={-1} aria-hidden="true">
          {a.thumbUrl ? (
            <img src={a.thumbUrl} alt="" loading="lazy" className="h-12 w-16 rounded-[2px] bg-inset object-cover" />
          ) : (
            <span className="flex h-12 w-16 items-center justify-center rounded-[2px] border border-rule bg-sheet">
              <span className="label !text-[0.625rem]">{kindWord(a)}</span>
            </span>
          )}
        </Link>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
            <Link to={open} className="link min-w-0 break-words text-ink">
              {a.name}
            </Link>
            {a.addedRole === 'client' ? (
              <Tag tone="cedar">{a.mine ? 'Yours' : 'From the client'}</Tag>
            ) : staff ? (
              a.shared ? (
                <Tag tone="moss">Shared</Tag>
              ) : (
                <span className="label">NSB only</span>
              )
            ) : null}
          </div>
          <p className="meta mt-0.5">
            {facts.map((f, i) => (
              <Fragment key={i}>
                {i ? ' · ' : ''}
                {f}
              </Fragment>
            ))}
            {a.kind === 'link' && a.url ? (
              <>
                {' · '}
                <a href={a.url} target="_blank" rel="noreferrer" className="link">
                  Open in {kindWord(a) === 'Drive' ? 'Drive' : hostOf(a.url) || 'a new tab'}
                </a>
              </>
            ) : a.originalUrl ? (
              <>
                {' · '}
                <a href={a.originalUrl} className="link" download>
                  Download
                </a>
              </>
            ) : null}
          </p>
          {a.note ? <p className="mt-1 max-w-2xl whitespace-pre-line text-sm text-ink-2">{a.note}</p> : null}

          {(staff || a.mine) && !editing ? (
            <div className="mt-1 flex flex-wrap items-center gap-x-5 text-sm">
              {!staff || a.addedRole === 'client' ? null : a.shared ? (
                a.review?.shared ? (
                  <span className="meta">Shared for review with its iteration</span>
                ) : (
                  <Button tone="quiet" onClick={() => setConfirming('unshare')}>
                    Stop sharing
                  </Button>
                )
              ) : (
                <Button tone="quiet" busy={busy} onClick={() => void patch({ shared: true })}>
                  Share with the client
                </Button>
              )}
              <Button tone="quiet" onClick={() => setEditing(true)}>
                Edit
              </Button>
              {a.removable ? (
                <Button tone="quiet" onClick={() => setConfirming('remove')}>
                  Remove
                </Button>
              ) : null}
            </div>
          ) : null}
          {confirming === 'unshare' ? (
            <Confirm
              prompt="Stop sharing it? The client no longer sees it or its comments; all of it is kept."
              busy={busy}
              onYes={() => void patch({ shared: false })}
              onNo={() => setConfirming(null)}
            />
          ) : null}
          {confirming === 'remove' ? (
            <Confirm
              prompt="Remove it for good? It has no comments yet."
              busy={busy}
              onYes={() => void run(() => api<AssetDto[]>(`/projects/${projectId}/assets/${a.id}`, { method: 'DELETE' }))}
              onNo={() => setConfirming(null)}
            />
          ) : null}
          {editing ? <EditAsset asset={a} busy={busy} onSave={(body) => void patch(body).then((ok) => ok && setEditing(false))} onCancel={() => setEditing(false)} /> : null}
          <ErrorLine error={err} />
        </div>
      </div>
    </li>
  );
}

function EditAsset({ asset: a, busy, onSave, onCancel }: { asset: AssetDto; busy: boolean; onSave: (body: Record<string, unknown>) => void; onCancel: () => void }) {
  const [name, setName] = useState(a.name);
  const [url, setUrl] = useState(a.url ?? '');
  const [dated, setDated] = useState(a.dated ?? '');
  const [note, setNote] = useState(a.note ?? '');
  const valid = Boolean(name.trim()) && (a.kind !== 'link' || Boolean(url.trim()));
  const save = () => onSave({ name: name.trim(), ...(a.kind === 'link' ? { url: url.trim() } : {}), dated: dated || null, note: note.trim() || null });
  // Its changes not saved, on the bar at the window's foot, which saves as Save below does (P-29).
  useUnsaved({
    names: changedLabels({ name: a.name, url: a.url, dated: a.dated, note: a.note }, { name, url, dated, note }, [
      ['Name', (x) => x.name],
      ['Link', (x) => (a.kind === 'link' ? x.url : null)],
      ['The day it is from', (x) => x.dated],
      ['Note', (x) => x.note],
    ]),
    busy,
    canSave: valid,
    onSave: save,
    onDiscard: () => {
      setName(a.name);
      setUrl(a.url ?? '');
      setDated(a.dated ?? '');
      setNote(a.note ?? '');
    },
  });
  return (
    <div className="mt-3 max-w-2xl border border-rule bg-sheet p-4 sm:p-5">
      <Field label="Name">{(id) => <TextInput id={id} value={name} onChange={(e) => setName(e.target.value)} />}</Field>
      {a.kind === 'link' ? <Field label="Link">{(id) => <TextInput id={id} type="url" value={url} onChange={(e) => setUrl(e.target.value)} />}</Field> : null}
      <Field label="The day it is from" hint="Blank: the day it was added.">
        {(id) => <TextInput id={id} type="date" max={today()} value={dated} onChange={(e) => setDated(e.target.value)} />}
      </Field>
      <Field label="Note">{(id) => <textarea id={id} rows={2} className="field resize-y" value={note} onChange={(e) => setNote(e.target.value)} />}</Field>
      <div className="flex gap-5">
        <Button busy={busy} disabled={!valid} onClick={save}>
          Save
        </Button>
        <Button tone="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
